import {
  TableCell,
  TableBody,
  TableRow,
  TableHead,
  Button,
  Table,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FieldRow from 'components/FormElements/FieldRow';
import { Margin, Row } from 'components/Layout';
import {
  createInitialGridData,
  GRID_DATA_STORAGE_KEY,
  GridData,
  GridCellValue,
  GridRow,
} from './helpers/gridHelpersV2';
import { useNotification } from 'hooks/ui/useNotification';
import { alpha, numberToCurrencyString } from 'further-ui/utils';
import Heading from 'components/Heading';
import api from 'lib/trpcClient';
import { buildUploadSaveInput } from './helpers/mapUploadGridDataToV2';
import { sumBy } from 'lodash';
import { useLocalStorage } from 'usehooks-ts';
import { getQueryKey } from '@trpc/react-query';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import type { UploadSaveResultData, UploadSaveInputData } from './types';

const TotalInvestorTable = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 0, 6),
  '& > div': {
    padding: theme.spacing(2.5, 0),
  },
}));

const Note = styled('div')(({ theme }) => ({
  fontSize: theme.fontSizes?.lg ?? 16,
  color: theme.palette.text.darkBodyText ?? '#656565',
  marginBottom: theme.spacing(1),
}));

const ScrollableTable = styled('div')(({ theme }) => ({
  overflow: 'auto',
  padding: theme.spacing(8, 0),
  margin: theme.spacing(0, 0, 4),
  clear: 'both',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.grey[400], 0.1),
}));

const StyledHeadCell = styled(TableCell)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    minWidth: 120,
  },
  borderBottomColor: theme.palette.borderColor?.lightGrey ?? '#EDEDED',
  lineHeight: '22px',
  fontSize: theme.fontSizes?.sm ?? 14,
  fontWeight: 600,
  fontStyle: 'normal',
}));

const StyledBodyCell = styled(TableCell)(({ theme }) => ({
  borderBottomColor: theme.palette.borderColor?.lightGrey ?? '#EDEDED',
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(30),
}));

type Tranche = {
  _id: string;
  fundName: string;
  firmId: {
    firmName: string;
  };
};

type DropdownsData = Record<
  string,
  undefined | Array<{ label: string; value: string }>
>;

type PreviewRow = GridRow & {
  fullName?: string;
};

const hasValue = (v: GridCellValue) => {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
};

const isBlankRow = (row: GridRow) => {
  const hasIndividualIdentity =
    hasValue(row.first_name) &&
    hasValue(row.last_or_corporate_name) &&
    hasValue(row.identity_date);
  const hasLegalEntityIdentity =
    hasValue(row.last_or_corporate_name) && hasValue(row.identity_date);
  return !hasIndividualIdentity && !hasLegalEntityIdentity;
};

type Props = {
  totalInvestors: number;
  existingInvestorAccounts: Array<PreviewRow>;
  handleSubmitCallback: (result: UploadSaveResultData) => void;
  handleBack?: () => void;
  tranches: Array<Tranche>;
  firmId?: string;
  gridDropdownsData: DropdownsData;
};

const PreviewTableV2: React.FC<Props> = ({
  totalInvestors,
  existingInvestorAccounts,
  handleSubmitCallback,
  handleBack,
  firmId,
  gridDropdownsData,
  tranches,
}) => {
  const { error } = useNotification();
  const saveMutation = api.investorInvestmentUpload.uploadSave.useMutation(
    useHandleTrpcMutation<UploadSaveResultData, UploadSaveInputData>()({
      invalidations: [getQueryKey(api.investor.listInvestors), ['investments']],
      onSuccess: (uploadResult) => {
        if (uploadResult?.uploadId) {
          handleSubmitCallback(uploadResult);
        } else {
          error('Upload started but no upload id was returned.');
        }
      },
    }),
  );

  const loading = saveMutation.isPending;

  const [gridData] = useLocalStorage<GridData>(
    GRID_DATA_STORAGE_KEY,
    createInitialGridData(),
  );
  const handleSubmitClick = () => {
    saveMutation.mutate(
      buildUploadSaveInput(gridData, gridDropdownsData, firmId),
    );
  };

  const getInvestorName = (row: PreviewRow) => {
    const existingInvestor = existingInvestorAccounts.find(
      (investor) => String(investor.email) === String(row.email),
    );
    if (existingInvestor?.fullName) {
      return String(existingInvestor.fullName);
    }
    if (row.last_or_corporate_name && row.first_name) {
      return `${row.first_name} ${row.last_or_corporate_name}`;
    }
    return String(row.last_or_corporate_name ?? row.email ?? '');
  };

  return (
    <div>
      <Heading variant="h2">Upload preview</Heading>
      <TotalInvestorTable>
        <FieldRow key="1" title="Date">
          {new Date().toLocaleDateString()}
        </FieldRow>
        <FieldRow key="2" title="Investors">
          {totalInvestors}
        </FieldRow>
        <FieldRow key="3" title="Total subscription value being uploaded">
          {numberToCurrencyString(
            sumBy(
              gridData.filter((row) => !isBlankRow(row)),
              (row) => Number(row.investment_amount ?? 0),
            ),
          )}
        </FieldRow>
      </TotalInvestorTable>
      {existingInvestorAccounts?.length > 0 && (
        <>
          <Note>
            The following uploaded investors have a name (and identity date for
            individuals) that match an existing Further account. Upon clicking
            submit, the system will upload all uploaded subscriptions but will
            connect subscriptions made by the below investors with their
            existing Further accounts. All new and existing investors will be
            visible within your investor database.
          </Note>
          <ScrollableTable>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <StyledHeadCell>Name</StyledHeadCell>
                  <StyledHeadCell>Identity date</StyledHeadCell>
                  <StyledHeadCell>Email address</StyledHeadCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {existingInvestorAccounts.map((row) => {
                  return (
                    <TableRow key={String(row.index)}>
                      <StyledBodyCell>{getInvestorName(row)}</StyledBodyCell>
                      <StyledBodyCell>{row.identity_date}</StyledBodyCell>
                      <StyledBodyCell>{row.email}</StyledBodyCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollableTable>
        </>
      )}
      <Note>
        All investors and subscriptions will be uploaded upon clicking submit.
      </Note>

      <ScrollableTable>
        <Table>
          <StyledTableHead>
            <TableRow>
              <StyledHeadCell>Name</StyledHeadCell>
              <StyledHeadCell>Birth/registration date</StyledHeadCell>
              <StyledHeadCell>Tranche</StyledHeadCell>
              <StyledHeadCell>Subscription amount</StyledHeadCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {gridData
              .filter((row) => !isBlankRow(row))
              .map((row) => {
                const tranche = tranches?.find((t) => t._id === row.tranche);
                const trancheLabel = tranche
                  ? `${tranche.firmId.firmName} ${tranche.fundName}`
                  : '';

                return (
                  <TableRow key={String(row.index)}>
                    <StyledBodyCell>{getInvestorName(row)}</StyledBodyCell>
                    <StyledBodyCell>{row.identity_date}</StyledBodyCell>
                    <StyledBodyCell>{trancheLabel}</StyledBodyCell>
                    <StyledBodyCell>
                      {numberToCurrencyString(
                        Number(row.investment_amount ?? 0),
                      )}
                    </StyledBodyCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </ScrollableTable>
      <Margin top="sm" bottom="sm">
        <Row fullWidth justify="space-between" spacing="xs">
          <Button variant="outlined" onClick={handleBack}>
            Back
          </Button>
          <SubmitButton
            variant="contained"
            color="primary"
            onClick={handleSubmitClick}
            disabled={loading}
          >
            Submit
          </SubmitButton>
        </Row>
      </Margin>
    </div>
  );
};

export default PreviewTableV2;
